import React, { useContext, useState } from 'react';
import { BuildingContext } from '../../context/BuildingContext';
import '../../styles/components/ControlCenter.css';

// Shared UI components
const NumberControl = ({ label, field, min, max, value, handleChange }) => (
  <div className="compact-control number-control">
    <label>
      <span className="control-label">{label}:</span>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={handleChange(field)}
      />
    </label>
  </div>
);

const ToggleButton = ({ field, onText, offText, className = "", handleToggle, isActive }) => (
  <button 
    onClick={handleToggle(field)}
    className={`feature-toggle ${isActive ? 'active' : ''} ${className}`}
  >
    {isActive ? onText : offText}
  </button>
);

const Divider = () => <div className="section-divider"></div>;

const ControlGroup = ({ label, children, rightContent }) => (
  <div className="control-group">
    {label && (
      <div className="control-group-label">
        {label}
        {rightContent}
      </div>
    )}
    {children}
  </div>
);

const BuildingControlsLeft = React.memo(({ handleChange, handleToggle }) => {
  const { state } = useContext(BuildingContext);
  
  // Local state for collapsing each section
  const [buildingOpen, setBuildingOpen] = useState(true);
  const [groundFloorOpen, setGroundFloorOpen] = useState(true);
  const [facadeOpen, setFacadeOpen] = useState(true);
  const [windowsOpen, setWindowsOpen] = useState(true);
  const [balconiesOpen, setBalconiesOpen] = useState(false);
  const [awningsOpen, setAwningsOpen] = useState(false);
  const [roofOpen, setRoofOpen] = useState(false);
  
  // Local state for subsections
  const [doorOpen, setDoorOpen] = useState(true);
  const [vitrineOpen, setVitrineOpen] = useState(true);

  return (
    <div className="ControlColumn left-column">
      {/* Structure Section */}
      <div className="controlSection">
        <h3 onClick={() => setBuildingOpen(prev => !prev)} className="section-title">
          {buildingOpen ? '▾' : '▸'} Structure
        </h3>
        {buildingOpen && (
          <div className="vertical-controls">
            <NumberControl 
              label="Level Height" 
              field="levelHeight" 
              min={10} 
              max={25} 
              value={state.levelHeight}
              handleChange={handleChange}
            />
            <NumberControl 
              label="Land Depth" 
              field="depth" 
              min={30} 
              max={80} 
              value={state.depth}
              handleChange={handleChange}
            />
            <NumberControl 
              label="Level Number" 
              field="levelNum" 
              min={1} 
              max={10} 
              value={state.levelNum}
              handleChange={handleChange}
            />
          </div>
        )}
      </div>

      {/* Ground Floor */}
      <div className="controlSection">
        <h3 onClick={() => setGroundFloorOpen(prev => !prev)} className="section-title">
          {groundFloorOpen ? '▾' : '▸'} Ground Floor
        </h3>
        {groundFloorOpen && (
          <div className="subSection">
            <ControlGroup>
              <div className="control-row">
                <NumberControl 
                  label="Floor Height" 
                  field="groundFloorHeight" 
                  min={15} 
                  max={28} 
                  value={state.groundFloorHeight}
                  handleChange={handleChange}
                />
              </div>
            </ControlGroup>
            
            <h2 
              onClick={() => setDoorOpen(prev => !prev)}
              className={`${doorOpen ? 'expanded' : ''}`}
            >
              Main Door
            </h2>
            {doorOpen && (
              <ControlGroup>
                <div className="control-row">
                  <NumberControl 
                    label="Height" 
                    field="doorHeight" 
                    min={1} 
                    max={state.groundFloorHeight} 
                    value={state.doorHeight}
                    handleChange={handleChange}
                  />
                  <NumberControl 
                    label="Width" 
                    field="doorWidth" 
                    min={1} 
                    max={10} 
                    value={state.doorWidth}
                    handleChange={handleChange}
                  />
                </div>
                <div className="control-row">
                  <label>
                    Position:
                    <select value={state.doorPosition} onChange={handleChange('doorPosition')}>
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </label>
                </div>
              </ControlGroup>
            )}

            <Divider />

            {/* Vitrine section in Ground Floor */}
            <h2 
              onClick={() => setVitrineOpen(prev => !prev)}
              className={`${vitrineOpen ? 'expanded' : ''}`}
            >
              Vitrine
              <div>
                <ToggleButton 
                  field="showVitrine" 
                  onText="◼ Show" 
                  offText="◻ Show"
                  handleToggle={handleToggle}
                  isActive={state.showVitrine}
                />
              </div>
            </h2>
            {vitrineOpen && state.showVitrine && (
              <>
                <ControlGroup>
                  <div className="control-row">
                    <label>Vitrine Frames:</label>
                    <ToggleButton 
                      field="showVitrineFrames" 
                      onText="Show" 
                      offText="Hide"
                      handleToggle={handleToggle}
                      isActive={state.showVitrineFrames}
                    />
                  </div>
                  <div className="control-row">
                    <NumberControl 
                      label="Offset" 
                      field="vitrineOffset" 
                      min={1} 
                      max={3} 
                      value={state.vitrineOffset}
                      handleChange={handleChange}
                    />
                    {state.showVitrineFrames && (
                      <NumberControl 
                        label="Division" 
                        field="vitrineFrameDivision" 
                        min={1} 
                        max={4} 
                        value={state.vitrineFrameDivision}
                        handleChange={handleChange}
                      />
                    )}
                  </div>
                </ControlGroup>

                <ControlGroup 
                  label="Vitrine Awning"
                  rightContent={
                    <ToggleButton 
                      field="showVitrineAwning" 
                      onText="◼ On" 
                      offText="◻ Off"
                      handleToggle={handleToggle}
                      isActive={state.showVitrineAwning}
                    />
                  }
                >
                  {state.showVitrineAwning && (
                    <div className="control-row">
                      <NumberControl 
                        label="Depth" 
                        field="vitrineAwningDepth" 
                        min={1} 
                        max={5} 
                        value={state.vitrineAwningDepth}
                        handleChange={handleChange}
                      />
                      <NumberControl 
                        label="Height" 
                        field="vitrineAwningHeight" 
                        min={1} 
                        max={5} 
                        value={state.vitrineAwningHeight}
                        handleChange={handleChange}
                      />
                    </div>
                  )}
                </ControlGroup>
              </>
            )}
          </div>
        )}
      </div>

      {/* Facade Controls */}
      <div className="controlSection">
        <h3 onClick={() => setFacadeOpen(prev => !prev)} className="section-title">
          {facadeOpen ? '▾' : '▸'} Facade
        </h3>
        {facadeOpen && (
          <>
            {/* Windows Settings */}
            <div className="subSection">
              <h2 
                onClick={() => setWindowsOpen(prev => !prev)}
                className={`${windowsOpen ? 'expanded' : ''}`}
              >
                Windows
              </h2>
              
              {windowsOpen && (
                <ControlGroup>
                  <div className="control-row">
                    <label>Windows Frames:</label>
                    <ToggleButton 
                      field="showWindowFrames" 
                      onText="Show" 
                      offText="Hide"
                      handleToggle={handleToggle}
                      isActive={state.showWindowFrames}
                    />
                  </div>
                  <div className="control-row">
                    <label>
                      Configuration:
                      <select 
                        value={state.windowConfig}
                        onChange={handleChange('windowConfig')}
                        style={{ marginLeft: '5px', width: '120px' }}
                      >
                        <option value="config4">4 Windows</option>
                        <option value="config5">5 Windows</option>
                        <option value="config2Large">2 Large</option>
                        <option value="config6">6 Windows</option>
                      </select>
                    </label>
                  </div>
                  <div className="control-row">
                    <NumberControl 
                      label="Top" 
                      field="windowTop" 
                      min={0} 
                      max={state.levelHeight - 5} 
                      value={state.windowTop}
                      handleChange={handleChange}
                    />
                    <NumberControl 
                      label="Bottom" 
                      field="windowBottom" 
                      min={-5} 
                      max={10} 
                      value={state.windowBottom}
                      handleChange={handleChange}
                    />
                  </div>
                  <div className="control-row">
                    <NumberControl 
                      label="Inset" 
                      field="windowInset" 
                      min={0} 
                      max={3} 
                      value={state.windowInset}
                      handleChange={handleChange}
                    />
                  </div>
                  <div className="control-row side-windows-control">
                    <label>Side Windows:</label>
                    <div className="side-windows-buttons">
                      <ToggleButton 
                        field="showLeftSideWindows" 
                        onText="◼ Left" 
                        offText="◻ Left"
                        handleToggle={handleToggle}
                        isActive={state.showLeftSideWindows}
                      />
                      <ToggleButton 
                        field="showRightSideWindows" 
                        onText="◼ Right" 
                        offText="◻ Right"
                        handleToggle={handleToggle}
                        isActive={state.showRightSideWindows}
                      />
                    </div>
                  </div>
                </ControlGroup>
              )}
            </div>

            <Divider />

            {/* Balconies Settings */}
            <div className="subSection">
              <h2 
                onClick={() => setBalconiesOpen(prev => !prev)}
                className={`${balconiesOpen ? 'expanded' : ''}`}
              >
                Balconies
                <ToggleButton 
                  field="showBalconies" 
                  onText="◼ Show" 
                  offText="◻ Hide"
                  handleToggle={handleToggle}
                  isActive={state.showBalconies}
                />
              </h2>

              {balconiesOpen && state.showBalconies && (
                <ControlGroup>
                  <div className="control-row">
                    <NumberControl 
                      label="Depth" 
                      field="balconyDepth" 
                      min={1} 
                      max={5} 
                      value={state.balconyDepth}
                      handleChange={handleChange}
                    />
                    <NumberControl 
                      label="Height" 
                      field="balconyHeight" 
                      min={1} 
                      max={5} 
                      value={state.balconyHeight}
                      handleChange={handleChange}
                    />
                  </div>
                  <div className="control-row">
                    <NumberControl 
                      label="Offset" 
                      field="balconySideOffset" 
                      min={-5} 
                      max={5} 
                      value={state.balconySideOffset}
                      handleChange={handleChange}
                    />
                  </div>
                </ControlGroup>
              )}
            </div>

            <Divider />

            {/* Awnings Settings */}
            <div className="subSection">
              <h2 
                onClick={() => setAwningsOpen(prev => !prev)}
                className={`${awningsOpen ? 'expanded' : ''}`}
              >
                Awnings
                <ToggleButton 
                  field="showAwnings" 
                  onText="◼ Show" 
                  offText="◻ Hide"
                  handleToggle={handleToggle}
                  isActive={state.showAwnings}
                />
              </h2>

              {awningsOpen && state.showAwnings && (
                <ControlGroup>
                  <div className="control-row">
                    <NumberControl 
                      label="Depth" 
                      field="awningDepth" 
                      min={1} 
                      max={5} 
                      value={state.awningDepth}
                      handleChange={handleChange}
                    />
                    <NumberControl 
                      label="Height" 
                      field="awningHeight" 
                      min={1} 
                      max={5} 
                      value={state.awningHeight}
                      handleChange={handleChange}
                    />
                  </div>
                  <div className="control-row">
                    <NumberControl 
                      label="Offset" 
                      field="awningSideOffset" 
                      min={-5} 
                      max={5} 
                      value={state.awningSideOffset}
                      handleChange={handleChange}
                    />
                  </div>
                </ControlGroup>
              )}
            </div>
          </>
        )}
      </div>

      {/* Roof Controls */}
      <div className="controlSection">
        <h3 onClick={() => setRoofOpen(prev => !prev)} className="section-title">
          {roofOpen ? '▾' : '▸'} Roof
        </h3>
        {roofOpen && (
          <div className="subSection">
            {/* Roof is always enabled by default */}
            <ControlGroup>
              <div className="control-row">
                <NumberControl 
                  label="Awning" 
                  field="roofAwning" 
                  min={0} 
                  max={4} 
                  value={state.roofAwning}
                  handleChange={handleChange}
                />
              </div>
              <div className="control-row">
                <NumberControl 
                  label="Guard Rails" 
                  field="roofThickness" 
                  min={0} 
                  max={6} 
                  value={state.roofThickness}
                  handleChange={handleChange}
                />
              </div>
            </ControlGroup>

            <h2>
              Roof Access
              <ToggleButton 
                field="showRoofAccess" 
                onText="◼ Show" 
                offText="◻ Hide"
                handleToggle={handleToggle}
                isActive={state.showRoofAccess}
              />
            </h2>
            <ControlGroup>
              {state.showRoofAccess && (
                <>
                  <div className="control-row">
                    <NumberControl 
                      label="Width" 
                      field="roofAccessWidth" 
                      min={4} 
                      max={24} 
                      value={state.roofAccessWidth}
                      handleChange={handleChange}
                    />
                    <NumberControl 
                      label="Height" 
                      field="roofAccessHeight" 
                      min={4} 
                      max={15} 
                      value={state.roofAccessHeight}
                      handleChange={handleChange}
                    />
                  </div>
                  <div className="control-row">
                    <NumberControl 
                      label="Depth" 
                      field="roofAccessDepth" 
                      min={4} 
                      max={20} 
                      value={state.roofAccessDepth}
                      handleChange={handleChange}
                    />
                    <NumberControl 
                      label="Offset" 
                      field="roofAccessOffset" 
                      min={5} 
                      max={20} 
                      value={state.roofAccessOffset}
                      handleChange={handleChange}
                    />
                  </div>
                  <div className="control-row">
                    <NumberControl 
                      label="Door W" 
                      field="roofAccessDoorWidth" 
                      min={2} 
                      max={8} 
                      value={state.roofAccessDoorWidth}
                      handleChange={handleChange}
                    />
                    <NumberControl 
                      label="Door H" 
                      field="roofAccessDoorHeight" 
                      min={2} 
                      max={10} 
                      value={state.roofAccessDoorHeight}
                      handleChange={handleChange}
                    />
                  </div>
                </>
              )}
            </ControlGroup>
          </div>
        )}
      </div>
    </div>
  );
});

export default BuildingControlsLeft; 